import { type IToken } from 'chevrotain';
import {
  type ConnectorCstNode,
  type DiagramCstNode,
  type DiagramTypeCstNode,
  type LabelCstNode,
  type NodeCstNode,
  type PropertiesCstNode,
  type PropertyCstNode,
  type StatementCstNode,
  type StepCstNode,
  type SubDiagramCstNode,
} from '../types';
import { CommonVisitor } from './CommonParser';

// Extended token interface to include custom properties added during lexing
interface ExtendedToken extends IToken {
  precedingWhitespace?: ExtendedToken;
  trailingWhitespace?: ExtendedToken;
  payload?: string;
}

// Type to represent CST nodes with token properties
type TokenizedCstNode = {
  [key: string]: ExtendedToken[] | undefined;
};

// Traverse a syntax tree regenerating the source text.
class CommonTextGenerator extends CommonVisitor {
  constructor() {
    super();
    this.validateVisitor();
  }

  // Use the information on the token to regenerate the original source text.
  token(ctx: ExtendedToken | ExtendedToken[] | undefined): string {
    if (!ctx) return '';
    if (Array.isArray(ctx)) ctx = ctx[0];
    if (!ctx) return '';

    let whitespaceBefore = '';
    let whitespaceAfter = '';
    let precedingWhitespace = ctx.precedingWhitespace;
    while (precedingWhitespace) {
      whitespaceBefore = precedingWhitespace.image + whitespaceBefore;
      precedingWhitespace = precedingWhitespace.precedingWhitespace;
    }

    let trailingWhitespace = ctx.trailingWhitespace;
    while (trailingWhitespace) {
      whitespaceAfter = whitespaceAfter + trailingWhitespace.image;
      trailingWhitespace = trailingWhitespace.trailingWhitespace;
    }

    return whitespaceBefore + ctx.image + whitespaceAfter;
  }

  // Handle optional properties.
  optionalProperties(
    ctx: NodeCstNode | DiagramTypeCstNode | ConnectorCstNode
  ): string {
    if (ctx.properties) {
      return this.visit(ctx.properties);
    } else {
      return '';
    }
  }

  optionalLabel(ctx: NodeCstNode): string {
    if (ctx.label) {
      return this.visit(ctx.label);
    } else {
      return '';
    }
  }

  diagram(ctx: DiagramCstNode): string {
    return (ctx.statement || []).map(this.visit, this).join('');
  }

  statement(ctx: StatementCstNode & TokenizedCstNode): string {
    const stmt = ctx.step || ctx.diagramType;
    const newline = this.token(ctx.Newline);

    if (stmt) {
      return this.visit(stmt) + newline;
    } else {
      return newline;
    }
  }

  diagramType(ctx: DiagramTypeCstNode): string {
    return this.token(ctx.DiagramType) + this.optionalProperties(ctx);
  }

  step(ctx: StepCstNode): string {
    const connector = ctx.connector;

    if (connector) {
      return (
        this.visit(ctx.FromNode[0]) +
        this.visit(connector) +
        (ctx.ToNode?.[0] ? this.visit(ctx.ToNode[0]) : '')
      );
    } else {
      return this.visit(ctx.FromNode[0]);
    }
  }

  subDiagram(
    ctx: SubDiagramCstNode & TokenizedCstNode & { label?: LabelCstNode }
  ): string {
    return (
      this.optionalLabel(ctx) +
      this.token(ctx.LSquare) +
      this.optionalNewline(ctx.Newline1) +
      this.visit(ctx.diagram) +
      this.optionalNewline(ctx.Newline2) +
      this.token(ctx.RSquare)
    );
  }

  node(ctx: NodeCstNode): string {
    return (
      this.optionalLabel(ctx) +
      (ctx.subDiagram ? this.visit(ctx.subDiagram) : this.token(ctx.Text)) +
      this.optionalProperties(ctx)
    );
  }

  connector(ctx: ConnectorCstNode & TokenizedCstNode): string {
    let leftSide = '';
    let rightSide = '';
    let hasInfo = false;

    if (ctx.ArrowLeft) {
      leftSide = this.token(ctx.ArrowLeft);
    }
    if (ctx.ArrowRight) {
      rightSide = this.token(ctx.ArrowRight);
    }
    if (ctx.DashLong) {
      leftSide = this.token(ctx.DashLong);
    }
    if (ctx.DoubleArrow) {
      leftSide = this.token(ctx.DoubleArrow);
    }

    if (ctx.ArrowLeftLong) {
      hasInfo = true;
      leftSide = this.token(ctx.ArrowLeftLong);
      if (ctx.ArrowRightLong) {
        rightSide = this.token(ctx.ArrowRightLong);
      } else {
        rightSide = this.token(ctx.DashRight);
      }
    } else if (ctx.DashLeft) {
      hasInfo = true;
      leftSide = this.token(ctx.DashLeft);
      if (ctx.ArrowRightLong) {
        rightSide = this.token(ctx.ArrowRightLong);
      } else {
        rightSide = this.token(ctx.DashRight);
      }
    }

    if (ctx.ArrowRightLong) {
      hasInfo = true;
      rightSide = this.token(ctx.ArrowRightLong);
      if (ctx.ArrowRightLong) {
        rightSide = this.token(ctx.ArrowRightLong);
      } else {
        rightSide = this.token(ctx.DashRight);
      }
    }

    if (hasInfo) {
      return (
        leftSide +
        this.token(ctx.Text) +
        this.optionalProperties(ctx) +
        rightSide
      );
    } else {
      return leftSide + rightSide;
    }
  }

  properties(ctx: PropertiesCstNode & TokenizedCstNode): string {
    return (
      this.token(ctx.LCurly) +
      this.optionalNewline(ctx.Newline1) +
      (ctx.property || [])
        .map((propertyCtx, index: number) => {
          let commaStr = '';
          if (ctx.Comma && ctx.Comma[index]) {
            commaStr = this.token(ctx.Comma[index]);
          }
          return this.visit(propertyCtx) + commaStr;
        })
        .join('') +
      this.optionalNewline(ctx.Newline2) +
      this.token(ctx.RCurly)
    );
  }

  property(ctx: PropertyCstNode & TokenizedCstNode): string {
    return (
      this.optionalNewline(ctx.Newline1) +
      this.token(ctx.NameText) +
      this.optionalNewline(ctx.Newline2) +
      this.token(ctx.Colon) +
      this.optionalNewline(ctx.Newline3) +
      this.token(ctx.ValueText) +
      this.optionalNewline(ctx.Newline4)
    );
  }

  label(ctx: LabelCstNode & TokenizedCstNode): string {
    return this.token(ctx.Text) + this.token(ctx.Colon);
  }

  optionalNewline(token: ExtendedToken[] | undefined): string {
    if (token && token[0]) {
      return this.token(token);
    } else {
      return '';
    }
  }
}

// We only need a single interpreter instance because our interpreter has no state.
const commonTextGenerator = new CommonTextGenerator();

export default commonTextGenerator;
export { CommonTextGenerator };
